import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_BASE_URL } from "../config";
import {
  IndianRupee, Receipt, BarChart3, ChevronDown, Calendar
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import "./Reports.css";
import Navbar from "../components/Navbar";

function Reports() {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [viewMode, setViewMode] = useState("daily");
  const [allSales, setAllSales] = useState([]); 
  const [reportData, setReportData] = useState({
    totalSales: 0,
    gstSales: 0,
    nonGstSales: 0,
    orders: []
  });

  const fetchSales = async () => {
    try {
      const userId = localStorage.getItem("user_id") || 0; // Get user_id from login
      const res = await axios.get(`${API_BASE_URL}/reports.php?user_id=${userId}`);
      setAllSales(res.data);
    } catch (err) {
      console.error("Error fetching sales history");
    }
  };

  useEffect(() => {
    fetchSales();
  }, []);

  useEffect(() => {
    const today = new Date(selectedDate);
    
    const filteredByDate = allSales.filter(order => {
      const orderDate = new Date(order.sale_date);
      if (viewMode === "daily") return order.sale_date === selectedDate;
      if (viewMode === "weekly") {
        const day = today.getDay();
        const diff = today.getDate() - day + (day === 0 ? -6 : 1);
        const monday = new Date(new Date(selectedDate).setDate(diff));
        monday.setHours(0,0,0,0);
        const nextMonday = new Date(monday);
        nextMonday.setDate(monday.getDate() + 7);
        return orderDate >= monday && orderDate < nextMonday;
      }
      if (viewMode === "monthly") {
        return orderDate.getMonth() === today.getMonth() && 
               orderDate.getFullYear() === today.getFullYear();
      }
      if (viewMode === "yearly") {
        return orderDate.getFullYear() === today.getFullYear();
      }
      return false;
    });

    const formattedOrders = filteredByDate.map((bill) => {
      const sameCategorySales = allSales.filter(s => s.table_id === bill.table_id);
      const categorySpecificIndex = sameCategorySales.length - sameCategorySales.indexOf(bill);
      const seq = categorySpecificIndex.toString().padStart(4, '0');
      
      const displayId = bill.table_id === "Walk-in" 
        ? `S-WALK-${seq}` 
        : `T${bill.table_id}-${seq}`;
      
      return { 
          ...bill, 
          displayId 
      };
    });

    let total = 0, gstSum = 0, nonGstSum = 0;
    formattedOrders.forEach(order => {
      const amount = parseFloat(order.grand_total);
      total += amount;
      if (parseFloat(order.gst_amount) > 0) gstSum += amount;
      else nonGstSum += amount;
    });

    setReportData({ 
      totalSales: total, 
      gstSales: gstSum, 
      nonGstSales: nonGstSum, 
      orders: formattedOrders 
    });
  }, [selectedDate, viewMode, allSales]);

  return (
    <div className="page-layout">
      <Navbar />
      <div className="main-content">
        <div className="reports-container">

        <header className="reports-header-ui">
          <div className="title-group">
            <h1 className="serif-title">Reports</h1>
            <p className="subtitle">Sales reports and analytics</p>
          </div>
          
          <div className="header-controls-group">
            <div className="view-select-wrapper header-dropdown">
              <select value={viewMode} onChange={(e) => setViewMode(e.target.value)} className="heading-select">
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="yearly">Yearly</option>
              </select>
              <ChevronDown size={14} className="dropdown-arrow" />
            </div>
            <div className="date-picker-ui">
              <span>Date:</span>
              <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} />
              <Calendar size={18} color="#71717A" />
            </div>
          </div>
        </header>

        <div className="stats-grid-ui">
          <div className="stat-card-ui">
            <div className="stat-icon-wrapper orange-tint">
              <IndianRupee size={24} color="#BA5028" />
            </div>
            <div className="stat-details">
              <p>Total Sales</p>
              <h3>₹{reportData.totalSales.toLocaleString()}</h3>
            </div>
          </div>
          
          <div className="stat-card-ui">
            <div className="stat-icon-wrapper green-tint">
              <Receipt size={24} color="#166534" />
            </div>
            <div className="stat-details">
              <p>GST Sales</p>
              <h3>₹{reportData.gstSales.toLocaleString()}</h3>
            </div>
          </div>
          
          <div className="stat-card-ui">
            <div className="stat-icon-wrapper brown-tint">
              <BarChart3 size={24} color="#BA5028" />
            </div>
            <div className="stat-details">
              <p>Non-GST Sales</p>
              <h3>₹{reportData.nonGstSales.toLocaleString()}</h3>
            </div>
          </div>
        </div>

        {/* DESKTOP TABLE VIEW */}
        <div className="report-table-box d-none d-md-block">
          <h2 className="table-header-title">
            {viewMode === "daily" ? "Daily Orders" : 
             viewMode === "weekly" ? "Weekly Orders" : 
             viewMode === "monthly" ? "Monthly Orders" : "Yearly Orders"}
          </h2>
          
          <table className="ui-data-table">
            <thead>
              <tr>
                <th style={{ width: '20%' }}>Order ID</th>
                <th style={{ width: '20%' }}>Table</th>
                <th style={{ width: '20%' }}>Items</th>
                <th style={{ width: '15%' }}>Amount</th>
                <th style={{ width: '10%' }}>GST</th>
                <th style={{ width: '15%' }}>Time</th>
              </tr>
            </thead>
            <tbody>
              {reportData.orders.length > 0 ? (
                reportData.orders.map((order) => {
                  return (
                    <tr key={order.id}>
                      <td className="bold-id">#{order.displayId}</td>
                      <td>{order.table_id === "Walk-in" ? "Walk-in" : `Table ${order.table_id}`}</td>
                      <td>{order.items_count} items</td>
                      <td className="bold-amount">₹{order.grand_total}</td>
                      <td>
                        <span className={parseFloat(order.gst_amount) > 0 ? "gst-badge-rust" : "gst-badge-muted"}>
                          {parseFloat(order.gst_amount) > 0 ? "GST" : "Non-GST"}
                        </span>
                      </td>
                      <td className="time-text">{order.sale_time}</td>
                    </tr>
                  );
                })
              ) : (
                <tr><td colSpan="6" style={{ textAlign: 'center', padding: '40px 0', color: '#9CA3AF' }}>No sales found for this period.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* MOBILE CARD VIEW (Replaces Table on small screens) */}
        <div className="mobile-reports-list d-md-none">
          {reportData.orders.length > 0 ? (
            reportData.orders.map((order) => {
              const [y, m, d] = order.sale_date.split("-");
              return (
                <div className="mobile-report-card" key={order.id}>
                  <div className="card-header">
                    <div className="bill-main">
                      <span className="mobile-label">BILL NO</span>
                      <span className="mobile-id">{order.displayId}</span>
                    </div>
                    <span className={parseFloat(order.gst_amount) > 0 ? "badge-gst" : "badge-nongst"}>
                      {parseFloat(order.gst_amount) > 0 ? "GST" : "Non-GST"}
                    </span>
                  </div>
                  
                  <div className="card-body">
                    <div className="info-item">
                      <span className="mobile-label">TABLE</span>
                      <span className="mobile-value">{order.table_id === "Walk-in" ? "Walk-in" : `Table ${order.table_id}`}</span>
                    </div>
                    <div className="info-item">
                      <span className="mobile-label">ITEMS</span>
                      <span className="mobile-value">{order.items_count} items</span>
                    </div>
                  </div>

                  <div className="card-footer">
                    <div className="info-item">
                      <span className="mobile-label">DATE & TIME</span>
                      <span className="mobile-time">{`${d}-${m}-${y} | ${order.sale_time}`}</span>
                    </div>
                    <div className="amount-section">
                      <span className="mobile-label">AMOUNT</span>
                      <span className="mobile-grand-total">₹{order.grand_total}</span>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="no-data-msg">No sales found for this period.</div>
          )}
        </div>

        </div>
      </div>
    </div>
  );
}

export default Reports;